import React from 'react';
import {
  FileText, Download, Video, Trash2, File, Play, Pencil,
  Link as LinkIcon, Image as ImageIcon, ExternalLink
} from 'lucide-react';
import { formatDate } from '../../utils/formatters';
import resourcesApi, { Resource, youtubeId, driveId } from '../../services/resourcesApi';

export const TYPE_LABEL: Record<string, string> = {
  document: 'Documento', video: 'Video', link: 'Enlace', image: 'Imagen', other: 'Otro',
};

export const typeIcon = (type: string, size = 24) => {
  const icons: Record<string, React.ReactElement> = {
    document: <FileText size={size} />,
    video: <Video size={size} />,
    link: <LinkIcon size={size} />,
    image: <ImageIcon size={size} />,
    other: <File size={size} />,
  };
  return icons[type] || <File size={size} />;
};

export const formatFileSize = (bytes?: number | null) => {
  if (!bytes) return null;
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

export const downloadResource = async (r: Resource) => {
  const response = await resourcesApi.download(r.id);
  const url = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', r.file_name || r.title);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

interface Props {
  resource: Resource;
  onPlay?: (r: Resource) => void;
  onDownloaded?: () => void;
  onDelete?: (r: Resource) => void;
  onEdit?: (r: Resource) => void;
}

const ResourceCard: React.FC<Props> = ({ resource: r, onPlay, onDownloaded, onDelete, onEdit }) => {
  const isEmbeddable = !!youtubeId(r.url) || !!driveId(r.url);
  const isVideoFile = r.type === 'video' && !!r.file_path;

  const handleDownload = async () => {
    try { await downloadResource(r); onDownloaded?.(); }
    catch (e) { console.error('Error downloading resource:', e); }
  };

  return (
    <div className="ses-card res-card">
      <div className="res-card-icon">{typeIcon(r.type)}</div>

      <div className="ses-card-body">
        <div className="ses-card-tpl">{TYPE_LABEL[r.type] || r.type}</div>
        <div className="ses-card-obj">{r.title}</div>
        {r.description
          ? <div className="ses-card-note">{r.description}</div>
          : onEdit && <div className="ses-card-note res-missing">Sin descripción — agrégale una para que se entienda de qué trata.</div>}

        {r.tags?.length > 0 && (
          <div className="res-tags">
            {r.tags.map(t => <span className="res-tag" key={t.id}>{t.name}</span>)}
          </div>
        )}

        <div className="res-card-meta">
          <span>{formatDate(r.created_at)}</span>
          {(r.uploader_first_name || r.uploader_last_name) &&
            <span>· {r.uploader_first_name} {r.uploader_last_name}</span>}
          {formatFileSize(r.file_size) && <span>· {formatFileSize(r.file_size)}</span>}
          {!!r.file_path && <span>· {r.downloads} descarga(s)</span>}
        </div>
      </div>

      <div className="ses-card-actions">
        {isEmbeddable || isVideoFile ? (
          <button className="btn-ink" onClick={() => onPlay?.(r)}>
            <Play size={14} /> Ver video
          </button>
        ) : r.url ? (
          <a className="btn-ink" href={r.url} target="_blank" rel="noopener noreferrer">
            <ExternalLink size={14} /> Abrir
          </a>
        ) : (
          <button className="btn-ink" onClick={handleDownload}>
            <Download size={14} /> Descargar
          </button>
        )}

        {isVideoFile && (
          <button className="btn-ghost btn-icon" onClick={handleDownload} title="Descargar archivo">
            <Download size={14} />
          </button>
        )}

        {onEdit && (
          <button className="btn-ghost btn-icon" onClick={() => onEdit(r)} title="Editar recurso">
            <Pencil size={14} />
          </button>
        )}

        {onDelete && (
          <button className="btn-ghost btn-icon" onClick={() => onDelete(r)} title="Eliminar recurso">
            <Trash2 size={14} />
          </button>
        )}
      </div>
    </div>
  );
};

export default ResourceCard;
